import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFeatureToggle from 'hooks/useFeatureToggle';
import {
  hasViewSites,
  hasManageDeviceNames,
  hasManagePasswordPolicy,
  hasManageUserProperties,
  hasViewAdminMenu,
  hasEditLiveCallRouting,
} from 'selectors/permissions';
import {
  getHasCompanySupervisorPermission,
  getIsCompanyAdmin,
} from 'selectors/loggedInUser';
import lazyLoad from 'utils/lazyLoad';
import { getPathsForGwtPlace, getRouterPath } from 'utils/navigation';
import { UnauthorizedPageAccess } from './utils';
import useProductPlanRoutes from './useProductPlanRoutes';

const DeviceManagementListPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "DeviceManagementListPage" */ 'pages/DeviceManagementListPage'
    ),
);
const DeviceNameDetailPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "DeviceNameDetail" */ 'pages/DeviceNameDetail'),
);
const GeneralSettingsPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "GeneralSettings" */ 'pages/GeneralSettings'),
);
const SiteListPage = lazyLoad(
  () => import(/* webpackChunkName: "SiteListPage" */ 'pages/SiteListPage'),
);
const HolidayListPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "HolidayListPage" */ 'pages/HolidayListPage'),
);
const LocationsAndLanguagesPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "LocationsAndLanguagesPage" */ 'pages/LocationsAndLanguagesPage'
    ),
);
const SecuritySettingsPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "SecuritySettingsPage" */ 'pages/SecuritySettingsPage'
    ),
);
const UserPropertiesPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "UserPropertiesPage" */ 'pages/UserPropertiesPage'
    ),
);
const ExternalDataPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "ExternalDataPage" */ 'pages/ExternalDataPage'),
);

const LiveCallRoutingPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "LiveCallRoutingPage" */ 'pages/LiveCallRoutingPage'
    ),
);

function RedirectToGeneralSettings() {
  return <Redirect to={getRouterPath('/GeneralSettingsPage')} />;
}

export default function useAdminRoutes() {
  const canManageDeviceNames = useSelector(hasManageDeviceNames);
  const canViewAdminMenu = useSelector(hasViewAdminMenu);
  const canViewSites = useSelector(hasViewSites);
  const isCompanyAdmin = useSelector(getIsCompanyAdmin);
  const canManageUserProperties = useSelector(hasManageUserProperties);
  const hasCompanySupervisorPermission = useSelector(
    getHasCompanySupervisorPermission,
  );
  const canManagePasswordPolicy = useSelector(hasManagePasswordPolicy);
  const isLiveCallRoutingMVPEnabled = useFeatureToggle('LIVE_CALL_ROUTING_MVP');
  const canViewLiveCallRouting =
    (useSelector(hasEditLiveCallRouting) || isCompanyAdmin) &&
    isLiveCallRoutingMVPEnabled;

  return [
    canManageDeviceNames && (
      <Route
        path={getPathsForGwtPlace('deviceManagement')}
        component={DeviceManagementListPage}
      />
    ),
    canManageDeviceNames && (
      <Route
        path={getRouterPath('/DeviceNameDetailPlace/:deviceType/:deviceUuid?')}
        component={DeviceNameDetailPage}
      />
    ),
    canViewAdminMenu && (
      <Route
        path={getRouterPath('/BasicCompanyDetailsPlace:all')}
        component={RedirectToGeneralSettings}
      />
    ),
    canViewAdminMenu && (
      <Route
        path={[
          getRouterPath('/GeneralSettingsPage'),
          getRouterPath('/generalSettingsPage'),
        ]}
        component={GeneralSettingsPage}
      />
    ),
    canViewSites && (
      <Route
        path={getPathsForGwtPlace('sites', 'sites?')}
        component={SiteListPage}
      />
    ),
    <Route
      path={getRouterPath('/LocationsAndLanguages:all')}
      component={LocationsAndLanguagesPage}
    />,
    (isCompanyAdmin ||
      hasCompanySupervisorPermission ||
      canManagePasswordPolicy) && (
      <Route
        path={getRouterPath('/SecuritySettingsPage')}
        component={SecuritySettingsPage}
      />
    ),
    (isCompanyAdmin ||
      hasCompanySupervisorPermission ||
      canManageUserProperties) && (
      <Route
        path={getRouterPath('/UserPropertiesPage')}
        component={UserPropertiesPage}
      />
    ),
    <Route
      path={getRouterPath('/ExternalDataPage')}
      component={ExternalDataPage}
    />,
    <Route path={getRouterPath('/Holidays')} component={HolidayListPage} />,
    <Route
      path={getRouterPath('/LiveCallRoutingPage')}
      component={
        canViewLiveCallRouting ? LiveCallRoutingPage : UnauthorizedPageAccess
      }
    />,
    ...useProductPlanRoutes(),
  ];
}
